import { useStaticQuery, graphql } from 'gatsby'
import React, { useContext } from 'react'
import { Helmet } from 'react-helmet'

import { LocaleContext } from '../locale/i18n'
import { SiteMetadata } from '../logic/shared-types'

const withTitleSuffix = (title: string) => `${title} | Measurlabs`

const Metadata = ({
  author,
  children,
  description,
  hideTitleSuffix,
  image,
  meta,
  noIndex,
  title,
}: {
  author?: string
  children?: React.ReactNode
  description?: string
  hideTitleSuffix?: boolean
  image?: string
  meta?: []
  noIndex?: boolean
  title?: string | null
}) => {
  const { locale } = useContext(LocaleContext)

  const { site } = useStaticQuery<{
    site: {
      siteMetadata: SiteMetadata
    }
  }>(graphql`
    query {
      site {
        siteMetadata {
          title
          description
          author
          image
          siteUrl
        }
      }
    }
  `)

  const metaDescription = description || site.siteMetadata.description
  const bareTitle = title || site.siteMetadata.title
  const suffixedTitle = hideTitleSuffix ? bareTitle : withTitleSuffix(bareTitle)

  return (
    <Helmet
      htmlAttributes={{
        lang: locale.split('-')[0],
      }}
      title={suffixedTitle}
      meta={[
        {
          content: metaDescription,
          name: `description`,
        },
        {
          content: suffixedTitle,
          name: 'title',
          property: `og:title`,
        },
        {
          content: metaDescription,
          property: `og:description`,
        },
        {
          content: `website`,
          property: `og:type`,
        },
        {
          content: image || site.siteMetadata.image,
          name: `og:image`,
          property: `og:image`,
        },
        {
          content: `1280`,
          property: `og:image:width`,
        },
        {
          content: `720`,
          property: `og:image:height`,
        },
        {
          content: `summary_large_image`,
          name: `twitter:card`,
        },
        {
          content: `@measurlabs`,
          name: `twitter:creator`,
        },
        { content: '@measurlabs', name: `twitter:site` },
        {
          content: title || '',
          name: `twitter:title`,
        },
        {
          content: metaDescription,
          name: `twitter:description`,
        },
        {
          content: image || site.siteMetadata.image,
          name: `twitter:image`,
        },
        {
          content: author || site.siteMetadata.author,
          name: `author`,
        },
        ...(noIndex ? [{ content: 'noindex', name: 'robots' }] : []),
      ].concat(meta || [])}
    >
      {children}
    </Helmet>
  )
}

export default Metadata
